import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import BigNumber from 'bignumber.js';
import moment from 'moment';

import { CalculationTypes, type PeriodSizes } from '../../lib/constants';
import DateRange from '../../lib/dateRange';
import {
  type Measure,
  type MetricSeriesResult,
  type MeasureErrors,
  type MeasureDataPoint,
} from '../../types';
import { type SoqlFilter } from 'common/components/FilterBar/SoqlFilter';

import { filterWhereClauses } from '../helpers';
import { setupBatchCalculation, executeBatchedQuery, formatPeriodKey, formatFiscalYearKey } from './core';
import { type SoqlDataProvider } from 'common/visualizations/dataProviders';

/**
 * Batch calculation for rate measures across multiple reporting periods.
 * Reduces query count from 2×N to 2 queries for N reporting periods.
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Base configuration for batch rate calculations
 */
interface BatchRateConfig {
  aggregationType: string;
  dataProvider: SoqlDataProvider;
  dateColumn: string;
  adjustedReportingPeriods: DateRange[];
  periodSize: PeriodSizes;
  fiscalYearStartMonth?: number;
}

/**
 * Configuration for creating partial results
 */
interface PartialResultsConfig {
  reportingPeriods: DateRange[];
  adjustedReportingPeriods: DateRange[];
  errors: MeasureErrors;
  aggregationType: string;
  periodSize?: PeriodSizes;
  fiscalYearStartMonth?: number;
}

/**
 * Configuration for creating fixed denominator maps
 */
interface FixedDenominatorConfig {
  fixedDenominator: string;
  adjustedReportingPeriods: DateRange[];
  periodSize: PeriodSizes;
  fiscalYearStartMonth?: number;
}

/**
 * Configuration for numerator-only calculations
 */
interface NumeratorOnlyConfig extends BatchRateConfig {
  numeratorFilters: SoqlFilter[];
  numeratorColumn?: string;
  reportingPeriods: DateRange[];
  errors: MeasureErrors;
}

/**
 * Configuration for denominator-only calculations
 */
interface DenominatorOnlyConfig extends BatchRateConfig {
  denominatorFilters: SoqlFilter[];
  denominatorColumn?: string;
  fixedDenominator?: string;
  reportingPeriods: DateRange[];
  errors: MeasureErrors;
}

// ============================================================================
// UTILITY HELPERS
// ============================================================================

/**
 * Creates a period key for the given period and configuration
 */
function createPeriodKey(
  period: DateRange | moment.Moment,
  periodSize: PeriodSizes,
  fiscalYearStartMonth?: number,
): string {
  const periodStart = period instanceof DateRange ? period.start : period;

  return periodSize === 'year' && fiscalYearStartMonth !== undefined
    ? formatFiscalYearKey(periodStart, fiscalYearStartMonth)
    : formatPeriodKey(periodStart, periodSize);
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validates that the rate measure configuration is correct for batch processing
 */
function validateRateConfiguration(
  aggregationType: string,
  numeratorColumn?: string,
  denominatorColumn?: string,
  fixedDenominator?: string,
): { numeratorOk: boolean; denominatorOk: boolean } {
  const isCountAggregation = aggregationType === CalculationTypes.COUNT;
  const numeratorOk = isCountAggregation || !!numeratorColumn;
  const denominatorOk = isCountAggregation || !!(denominatorColumn || !isEmpty(fixedDenominator));

  return { numeratorOk, denominatorOk };
}

// ============================================================================
// QUERY EXECUTION HELPERS
// ============================================================================

/**
 * Executes the numerator query for rate calculations
 */
async function executeNumeratorQuery(
  config: { numeratorFilters: SoqlFilter[]; numeratorColumn?: string } & BatchRateConfig,
): Promise<Map<string, BigNumber>> {
  const numeratorWhereClause = filterWhereClauses(config.numeratorFilters);
  const isCountAggregation = config.aggregationType === CalculationTypes.COUNT;

  if (isCountAggregation || (config.aggregationType === CalculationTypes.SUM && config.numeratorColumn)) {
    const queryConfig = {
      column: isCountAggregation ? '' : config.numeratorColumn || '',
      queryType: isCountAggregation ? ('count' as const) : ('sum' as const),
    };

    return executeBatchQuery(
      config.dataProvider,
      queryConfig.column,
      config.dateColumn,
      numeratorWhereClause,
      config.adjustedReportingPeriods,
      config.periodSize,
      queryConfig.queryType,
      config.fiscalYearStartMonth,
    );
  } else {
    throw new Error(`Invalid numerator configuration for aggregation type: ${config.aggregationType}`);
  }
}

/**
 * Creates a fixed denominator map for all reporting periods
 */
function createFixedDenominatorMap(config: FixedDenominatorConfig): Map<string, BigNumber> {
  const fixedValue = new BigNumber(config.fixedDenominator);
  const denominatorMap = new Map<string, BigNumber>();

  config.adjustedReportingPeriods.forEach((period) => {
    const periodKey = createPeriodKey(period, config.periodSize, config.fiscalYearStartMonth);
    denominatorMap.set(periodKey, fixedValue);
  });

  return denominatorMap;
}

/**
 * Executes the denominator query for rate calculations
 */
async function executeDenominatorQuery(
  config: {
    denominatorFilters: SoqlFilter[];
    denominatorColumn?: string;
    fixedDenominator?: string;
  } & BatchRateConfig,
): Promise<Map<string, BigNumber>> {
  if (config.fixedDenominator && !isEmpty(config.fixedDenominator)) {
    return createFixedDenominatorMap({
      fixedDenominator: config.fixedDenominator,
      adjustedReportingPeriods: config.adjustedReportingPeriods,
      periodSize: config.periodSize,
      fiscalYearStartMonth: config.fiscalYearStartMonth,
    });
  }

  const denominatorWhereClause = filterWhereClauses(config.denominatorFilters);
  const isCountAggregation = config.aggregationType === CalculationTypes.COUNT;

  if (isCountAggregation || (config.aggregationType === CalculationTypes.SUM && config.denominatorColumn)) {
    const queryConfig = {
      column: isCountAggregation ? '' : config.denominatorColumn || '',
      queryType: isCountAggregation ? ('count' as const) : ('sum' as const),
    };

    return executeBatchQuery(
      config.dataProvider,
      queryConfig.column,
      config.dateColumn,
      denominatorWhereClause,
      config.adjustedReportingPeriods,
      config.periodSize,
      queryConfig.queryType,
      config.fiscalYearStartMonth,
    );
  } else {
    throw new Error(`Invalid denominator configuration for aggregation type: ${config.aggregationType}`);
  }
}

// ============================================================================
// RESULT PROCESSING HELPERS
// ============================================================================

/**
 * Creates partial result objects for periods with configuration errors
 */
function createPartialResults(
  config: PartialResultsConfig,
  numeratorResults?: Map<string, BigNumber>,
  denominatorResults?: Map<string, BigNumber>,
): Omit<MetricSeriesResult, 'date'>[] {
  const configErrors = { ...config.errors, calculationNotConfigured: true };

  return config.reportingPeriods.map((period, index) => {
    let numerator = null;
    let denominator = null;

    if (numeratorResults && config.adjustedReportingPeriods && config.periodSize !== undefined) {
      const adjustedPeriod = config.adjustedReportingPeriods[index];
      if (adjustedPeriod) {
        const periodKey = createPeriodKey(adjustedPeriod, config.periodSize, config.fiscalYearStartMonth);
        numerator = numeratorResults.get(periodKey) || null;
      }
    }

    if (denominatorResults && config.adjustedReportingPeriods && config.periodSize !== undefined) {
      const adjustedPeriod = config.adjustedReportingPeriods[index];
      if (adjustedPeriod) {
        const periodKey = createPeriodKey(adjustedPeriod, config.periodSize, config.fiscalYearStartMonth);
        denominator = denominatorResults.get(periodKey) || null;
      }
    }

    const periodErrors = {
      ...configErrors,
      ...(denominatorResults && denominator !== null && { dividingByZero: denominator.isZero() }),
      notEnoughData: true,
    };

    return {
      errors: periodErrors,
      result: {
        value: config.aggregationType === CalculationTypes.COUNT ? new BigNumber(0) : null,
        numerator,
        denominator,
      },
    };
  });
}

/**
 * Handles numerator-only calculation (invalid denominator case)
 */
async function handleNumeratorOnlyCalculation(
  config: NumeratorOnlyConfig,
): Promise<Omit<MetricSeriesResult, 'date'>[]> {
  const numeratorResults = await executeNumeratorQuery(config);

  return createPartialResults(config, numeratorResults, undefined);
}

/**
 * Handles denominator-only calculation (invalid numerator case)
 */
async function handleDenominatorOnlyCalculation(
  config: DenominatorOnlyConfig,
): Promise<Omit<MetricSeriesResult, 'date'>[]> {
  let denominatorResults: Map<string, BigNumber>;

  if (config.fixedDenominator && !isEmpty(config.fixedDenominator)) {
    denominatorResults = createFixedDenominatorMap({
      fixedDenominator: config.fixedDenominator,
      adjustedReportingPeriods: config.adjustedReportingPeriods,
      periodSize: config.periodSize,
      fiscalYearStartMonth: config.fiscalYearStartMonth,
    });
  } else {
    denominatorResults = await executeDenominatorQuery(config);
  }

  return createPartialResults(config, undefined, denominatorResults);
}

/**
 * Combines numerator and denominator results to calculate rate for a single period
 */
function calculatePeriodRate(
  period: DateRange,
  numeratorResults: Map<string, BigNumber>,
  denominatorResults: Map<string, BigNumber>,
  periodSize: PeriodSizes,
  fiscalYearStartMonth: number | undefined,
  aggregationType: string,
  asPercent: boolean,
  errors: MeasureErrors,
): { errors: MeasureErrors; result: MeasureDataPoint } {
  const periodKey = createPeriodKey(period, periodSize, fiscalYearStartMonth);

  const numerator = numeratorResults.get(periodKey);
  const denominator = denominatorResults.get(periodKey);

  // Build result object similar to individual rate calculation
  const result = {
    value: aggregationType === CalculationTypes.COUNT ? new BigNumber(0) : null,
    numerator: null as BigNumber | null,
    denominator: null as BigNumber | null,
  };

  const periodErrors = { ...errors, calculationNotConfigured: false };

  // Set numerator if available
  if (numerator !== undefined) {
    result.numerator = numerator;
  }

  // Set denominator if available
  if (denominator !== undefined) {
    result.denominator = denominator;
    periodErrors.dividingByZero = denominator.isZero();
  }

  // Calculate rate if both numerator and denominator are available
  if (numerator !== undefined && denominator !== undefined) {
    if (!periodErrors.dividingByZero) {
      result.value = numerator.dividedBy(denominator).times(asPercent ? '100' : '1');
    }
  } else {
    periodErrors.notEnoughData = true;
  }

  return {
    errors: periodErrors,
    result,
  };
}

// ============================================================================
// MAIN EXPORT FUNCTION
// ============================================================================

/**
 * Calculates rate measures using batch queries for multiple reporting periods.
 * Executes separate batched queries for numerator and denominator, then combines results.
 */
export const calculateBatchedRateMeasures = async (
  measure: Measure | null,
  reportingPeriods: DateRange[],
): Promise<Omit<MetricSeriesResult, 'date'>[]> => {
  // Handle null measure
  if (!measure) {
    const errors = { calculationNotConfigured: true };
    return reportingPeriods.map(() => ({
      errors,
      result: {
        value: null,
        numerator: null,
        denominator: null,
      },
    }));
  }

  const setup = setupBatchCalculation(measure, reportingPeriods);
  if ('shouldReturn' in setup) {
    return reportingPeriods.map(() => ({
      errors: { ...setup.errors },
      result: {
        value: null,
        numerator: null,
        denominator: null,
      },
    }));
  }

  const {
    errors,
    measureWithFilters,
    adjustedReportingPeriods,
    dateColumn,
    periodSize,
    dataProvider,
    fiscalYearStartMonth,
  } = setup;

  // Extract rate-specific configuration
  const {
    aggregationType,
    numeratorColumn,
    numeratorFilters,
    denominatorColumn,
    denominatorFilters,
    fixedDenominator,
  } = get(measureWithFilters, 'metricConfig.arguments', {});

  const asPercent = get(measureWithFilters, 'metricConfig.display.asPercent');

  // Create base config object to avoid repetition
  const baseConfig = {
    aggregationType,
    dataProvider,
    dateColumn,
    adjustedReportingPeriods,
    periodSize,
    fiscalYearStartMonth,
    reportingPeriods,
    errors,
  };

  // Validate configuration
  const { numeratorOk, denominatorOk } = validateRateConfiguration(
    aggregationType,
    numeratorColumn,
    denominatorColumn,
    fixedDenominator,
  );

  // Handle configuration validation and return partial results when needed
  if (!numeratorOk && !denominatorOk) {
    return createPartialResults(baseConfig);
  }

  // Use case: Only numerator is valid
  if (numeratorOk && !denominatorOk) {
    try {
      return await handleNumeratorOnlyCalculation({
        ...baseConfig,
        numeratorFilters,
        numeratorColumn,
      });
    } catch (error) {
      console.warn('Batch rate calculation failed:', error);
      throw error;
    }
  }

  // Use case: Only denominator is valid
  if (!numeratorOk && denominatorOk) {
    try {
      return await handleDenominatorOnlyCalculation({
        ...baseConfig,
        denominatorFilters,
        denominatorColumn,
        fixedDenominator,
      });
    } catch (error) {
      console.warn('Batch rate calculation failed:', error);
      throw error;
    }
  }

  // Use case: Both numerator and denominator are valid - execute normal calculation
  try {
    const [numeratorResults, denominatorResults] = await Promise.all([
      executeNumeratorQuery({
        ...baseConfig,
        numeratorFilters,
        numeratorColumn,
      }),
      executeDenominatorQuery({
        ...baseConfig,
        denominatorFilters,
        denominatorColumn,
        fixedDenominator,
      }),
    ]);

    // Combine results and calculate rates for each period
    return adjustedReportingPeriods.map((period) =>
      calculatePeriodRate(
        period,
        numeratorResults,
        denominatorResults,
        periodSize,
        fiscalYearStartMonth,
        aggregationType,
        asPercent,
        errors,
      ),
    );
  } catch (error) {
    console.warn('Batch rate calculation failed:', error);
    throw error;
  }
};

/**
 * Helper function to execute a batched query and return results as a Map
 */
async function executeBatchQuery(
  dataProvider: SoqlDataProvider,
  column: string,
  dateColumn: string,
  whereClause: string[],
  reportingPeriods: DateRange[],
  periodSize: PeriodSizes,
  queryType: 'sum' | 'count',
  fiscalYearStartMonth?: number,
): Promise<Map<string, BigNumber>> {
  const batchData = await executeBatchedQuery(
    dataProvider,
    column,
    dateColumn,
    whereClause,
    reportingPeriods,
    periodSize,
    queryType,
    fiscalYearStartMonth,
  );

  const resultMap = new Map<string, BigNumber>();

  batchData.forEach((row) => {
    if (row.value !== null) {
      let periodKey: string;

      // Handle period key formatting based on period size
      if (periodSize === 'week' || periodSize === 'quarter' || periodSize === 'year') {
        periodKey = row.period; // Already correctly formatted from CASE statement
      } else {
        const periodDate = new Date(row.period);
        periodKey = formatPeriodKey(moment(periodDate), periodSize);
      }

      resultMap.set(periodKey, new BigNumber(row.value));
    }
  });

  // For count queries, missing periods should have value 0, not undefined
  if (queryType === 'count') {
    reportingPeriods.forEach((period) => {
      const periodKey = createPeriodKey(period, periodSize, fiscalYearStartMonth);

      if (!resultMap.has(periodKey)) {
        resultMap.set(periodKey, new BigNumber(0));
      }
    });
  }

  return resultMap;
}
